import { Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { first, take } from 'rxjs/operators';
import { ObjectUtil } from '../../../../../components/common/object-util/object-util';
import { WidgetDynamicBaseComponent } from '../../widgetDynamicBase';
import { Observable } from 'rxjs';

/**
 * Direct Cap Tenant Rollover Widget
 */
@Component({
  selector: 'tenant-rollover-widget',
  templateUrl: './tenant-rollover-widget.component.html',
  styleUrls: ['../../common/css/general.scss', './tenant-rollover-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TenantRolloverWidgetComponent extends WidgetDynamicBaseComponent implements OnInit {

  /**
   * Widget Guid
   */
  @Input() widgetGuid: string;

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    this.useDefaultServiceSaveChangesHandler("tenantRollover", this.provideSaveObject.bind(this));
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (!this.widgetConfig.hashtagSupported) {
      this.widgetConfig.hashtagSupported = true;
    }

    this.availableLayouts = this.widgetConfig.widgetSettings.availableLayouts
    this.currentLayout = this.widgetConfig.widgetSettings.currentLayout;

    if (this.currentLayout === "ChosenOnAdd") {
      this.chooseNextLayout();
    }

    this.mockupData$ = this.widgetService.loadMockup('tenantRollover').pipe(take(1));

    this.widgetData$ = this.mockup ?
      this.mockupData$
      : this.loadLayout(this.currentLayout);
  }

  /**
   * Constructor
   * @param injector
   * @param httpClient
   * @param appServiceHelper
   */
  constructor(injector: Injector) {
    super(injector, 'tenantRollover', true);
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.initWidgetConfig();
    this.widgetResized();
  }

  /**
   * Pre-process the loaded data.Override if needed
   * @param data The loaded data
   */
  protected preprocessLoadedData(data): any {
    this.prepareLayoutData(data, this.currentLayout);
    return data;
  }

  /**
   * Called by base class in save process. Widget should return object to be saved
   */
  async provideSaveObject(): Promise<any> {
    const data = ObjectUtil.deepCopy(this.widgetData);//deep copy

    return data;
  }

  /**
  * Choose next available layout when adding tenant rollover widget into report document
  */
  private chooseNextLayout() {
    const activeDocument = this.rdStorage.getActiveDocument();
    const usedLayouts: string[] = [];
    for (const page of activeDocument.data.pages) {
      for (const widget of page.widgets) {
        if (widget.widgetId === this.widgetConfig.widgetId) {//just tenant rolllover widgets
          if (usedLayouts.indexOf(widget.widgetSettings.currentLayout) < 0) {
            usedLayouts.push(widget.widgetSettings.currentLayout);
          }
        }
      }
    }

    for (const layout of this.availableLayouts) {
      if (usedLayouts.indexOf(layout) < 0) {
        this.currentLayout = layout;
        this.widgetConfig.widgetSettings.currentLayout = this.currentLayout;
        break;
      }
    }

    setTimeout(() => {
      this.eventBus.broadcastEvent('widgetsChanged');
    });
  }

  private prepareLayoutData(widgetData: any, layout: string) {
    const layoutId = parseInt(layout.slice(-1));

    const keys = Object.keys(widgetData).filter(k => k.includes('tenantRollover' + layoutId))
    for (let i = 0; i < keys.length; i++) {
      widgetData[keys[i].replace('tenantRollover' + layoutId, 'tenantRollover')] = widgetData[keys[i]];
    }

    for (let row = 0; row < widgetData.rows.length; row++) {
      const rowKeys = Object.keys(widgetData.rows[row]).filter(k => k.includes('tenantRollover' + layoutId))

      for (let i = 0; i < rowKeys.length; i++) {
        widgetData.rows[row][rowKeys[i].replace('tenantRollover' + layoutId, 'tenantRollover')] = widgetData.rows[row][rowKeys[i]];
      }
    }
  }

  /**
   * Load widget data for given layoutId
   * @param layout {number} Layout Id
   */
  loadLayout(layoutId: string): Observable<any> {
    return this.widgetService.loadFromWidgetResource(`tenantRollover/layout?id=${this.getReport()}&layoutId=${layoutId}`);
  }

  /**
   * Layout changed event handler
  * @param newLayout {string} New layout
   */
  layoutChanged(newLayout: string): void {
    this.widgetConfig.widgetSettings.currentLayout = newLayout;

    if (this.widgetData.reportId) {
      this.loadLayout(newLayout).pipe(first()).subscribe(data => {
        this.widgetData = this.preprocessLoadedData(data);
        this.prepareLayoutData(this.widgetData, newLayout);
        super.layoutChanged(newLayout);
      });
    }
   
  }

  /**
   * Called by base class to inform widget about save action result
   * @param _success {boolean} The result of save action
   * @param _err {any} The error object, if save action failed
   */
  saveCompleted(_success: boolean, _err: any = null): void {
    //do something if error
  }

  public getMinimumRowIndex(): number {
    return 1;
  }

  public getMaximumRowIndex(): number {
    if (this.widgetData) {
      return this.widgetData?.rows?.length + 7;
    }
    return -1;
  }

  public moreColumnsRequestedIfOnPortraitPage(): boolean {
    return false;
  }
}
